import { getScribeHttpClients } from '@heidi/api/client';

export type CuaCloudProviderKind = 'running' | 'waking' | 'paused' | 'unknown';

export type CuaCloudMachineStatus = 'provisioning' | 'ready' | 'failed';

const WAKING_PROVIDER_STATES = new Set([
  'starting',
  'creating',
  'restoring',
  'queued',
  'pending',
  'pulling',
  'resuming',
  'building',
]);

const PAUSED_PROVIDER_STATES = new Set(['stopped', 'archived', 'paused']);

/** Classify Daytona's live sandbox state for status copy and the live view. */
export const classifyCuaCloudProviderState = (
  state: string | undefined
): CuaCloudProviderKind => {
  if (state === 'started') return 'running';
  if (state && WAKING_PROVIDER_STATES.has(state)) return 'waking';
  if (state && PAUSED_PROVIDER_STATES.has(state)) return 'paused';
  return 'unknown';
};

export interface CuaCloudMachine {
  machine_id: string;
  status: CuaCloudMachineStatus;
  created_at: string;
}

export interface CuaCloudMachineDetails extends CuaCloudMachine {
  display_name: string | null;
  snapshot_name: string;
  placement_target: string;
  /** Live Daytona sandbox state: started, stopped, etc. */
  provider_state: string;
  /** Pause after N idle minutes; 0 keeps the machine always on. */
  auto_stop_minutes: number;
}

/** PATCH semantics: only fields present in the body are changed. */
// eslint-disable-next-line @heidi/rules/no-new-carved-domain-data -- Noun collision: "Settings" is the cloud VM's own settings payload (rename, auto-pause), not workspace/identity-projection data.
export interface CuaCloudSettingsUpdate {
  display_name?: string | null;
  auto_stop_minutes?: number;
}

// "Session" here is a noun collision with the VNC preview link, not Heidi
// session data — named "link" to keep it unambiguous.
export interface CuaCloudVncLink {
  url: string;
  expires_at: string;
}

const TIMEOUT = 30_000;
// Provisioning / start-stop calls hit the Daytona control plane and can be slow.
const SLOW_TIMEOUT = 90_000;
const http = () => getScribeHttpClients().scribeHttpV2;

export const cuaCloudClient = {
  ensureMachine: () =>
    http().put<CuaCloudMachine>('/rpa/cua-cloud/machine', undefined, {
      timeout: SLOW_TIMEOUT,
    }),
  getMachine: () =>
    http().get<CuaCloudMachine>('/rpa/cua-cloud/machine', { timeout: TIMEOUT }),
  getMachineDetails: () =>
    http().get<CuaCloudMachineDetails>('/rpa/cua-cloud/machine/details', {
      timeout: TIMEOUT,
    }),
  pauseMachine: () =>
    http().post<CuaCloudMachine>('/rpa/cua-cloud/machine:pause', undefined, {
      timeout: SLOW_TIMEOUT,
    }),
  resumeMachine: () =>
    http().post<CuaCloudMachine>('/rpa/cua-cloud/machine:resume', undefined, {
      timeout: SLOW_TIMEOUT,
    }),
  deleteMachine: () =>
    http().delete<void>('/rpa/cua-cloud/machine', { timeout: SLOW_TIMEOUT }),
  updateSettings: (settings: CuaCloudSettingsUpdate) =>
    http().patch<CuaCloudMachineDetails>('/rpa/cua-cloud/machine/settings', settings, {
      timeout: TIMEOUT,
    }),
  createVncLink: () =>
    http().post<CuaCloudVncLink>('/rpa/cua-cloud/machine:vnc-session', undefined, {
      timeout: SLOW_TIMEOUT,
    }),
};
